import { inject, Injectable, Signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { IDisplaySlide, SlideTypeCategory } from "@pcp/core/interfaces";
import { extractSlideTypeCategory } from "@pcp/core/util";
import { PCP_LOGGER } from "@pcp/logging";
import {
    ClassroomCommand,
    CommandType,
    isMutateRubberElementCommand,
    isVideoCommand,
    LessonHubFacade,
    UpdateSlideContentCommand,
    VideoCommand,
} from "@pcp/streaming";
import { Observable, ReplaySubject, skip } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { ClassroomFacade, SyncedLessonState } from "shared/classroom/data-access";
import { DomMutation } from "shared/dom-mutation-core";

export type LessonContentType =
    | 'image'
    | 'video'
    | 'htmlElement'
    | 'rubberElement'
    | 'difficultQuestion';

export type MutationCommand =
    | UpdateSlideContentCommand
    | VideoCommand;
const isNonYjsMutationCommand = (
    cmd: ClassroomCommand
): cmd is MutationCommand =>
    isMutateRubberElementCommand(cmd);

@Injectable({
    providedIn: 'root',
})
export class SlidePreviewCommandStreamService {
    private readonly log = inject(PCP_LOGGER).createScoped('slide-preview-cmd');
    private readonly lessonHub = inject(LessonHubFacade);
    private readonly lessonYjs = inject(ClassroomFacade);

    createPrimarySlideMutationCommandStream(): Observable<Observable<MutationCommand>> {
        return this.createMutationCommandStream(1, [
            this.lessonYjs.primarySlideShowingId,
        ]);
    }

    createSecondarySlideMutationCommandStream(): Observable<Observable<MutationCommand>> {
        return this.createMutationCommandStream(2, [
            this.lessonYjs.primarySlideShowingId,
            this.lessonYjs.secondarySlideShowingId,
        ]);
    }

    createTertiarySlideMutationCommandStream(): Observable<Observable<MutationCommand>> {
        return this.createMutationCommandStream(3, [
            this.lessonYjs.primarySlideShowingId,
            this.lessonYjs.secondarySlideShowingId,
            this.lessonYjs.tertiarySlideShowingId,
        ]);
    }

    private createMutationCommandStream(
        layer: 1 | 2 | 3,
        resetOnLayerShowingChange: Signal<string>[],
    ): Observable<Observable<MutationCommand>> {
        return new Observable((subscriber) => {
            let updateCommand$ = new ReplaySubject<MutationCommand>();

            const replayExistingAndEmit = (): void => {
                updateCommand$.complete();
                updateCommand$ = new ReplaySubject<MutationCommand>();
                // Replay existing Y.Array items (late-join / reconnect)
                const batches = this.lessonYjs.getMutationBatches(layer);
                if (batches.length > 0) {
                    const elementType = this.resolveElementType(layer);
                    for (const batch of batches) {
                        const cmd = this.batchToCommand(batch, layer, elementType);
                        if (cmd) updateCommand$.next(cmd);
                    }
                }
                subscriber.next(updateCommand$.asObservable());
            };

            replayExistingAndEmit();

            // SignalR: rubber element, video, and perception commands (NOT UPDATE_SLIDE)
            const signalrSubscription = this.lessonHub.Command.subscribe(
                (command) => {
                    if (isNonYjsMutationCommand(command) && command.layer === layer) {
                        updateCommand$.next(command);
                    }
                    if (isVideoCommand(command) && command.layer === layer) {
                        updateCommand$.next(command);
                    }
                }
            );

            // Yjs: DomMutation batches (replaces SignalR UPDATE_SLIDE)
            const batchAdded$ = this.lessonYjs.mutationBatchAdded$;
            if (!batchAdded$) {
                this.log.error('mutationBatchAdded$ is null — Yjs not initialized when mutation stream was created');
            }
            const yjsSubscription = batchAdded$?.subscribe(
                ({ layer: batchLayer, batch }) => {
                    if (batchLayer === layer) {
                        const cmd = this.batchToCommand(batch, layer);
                        if (cmd) updateCommand$.next(cmd);
                    }
                }
            );

            // A fresh showing on this layer (or on any layer above it, which cascades)
            // retires the buffered commands: `<layer>SlideShowingId` is re-minted by
            // `resetLayerForNewShowing` for `setSlide`, `removeSlide` AND the same-slide
            // `resetSlideToInitial` gesture alike, so one trigger covers navigation and
            // reset. It replaces the former slide-identity + `mutationBatchesCleared$`
            // pair: the cleared event only fires when a clear actually emptied a
            // non-empty Y.Array, so it stayed silent for a rubber slide (whose mutations
            // are SignalR-ephemeral and never enter the Y.Doc) and left erase commands
            // buffered for replay onto the freshly reset element.
            //
            // `skip(1)` avoids the initial Signal emission replaying on subscribe —
            // `replayExistingAndEmit()` already ran once above for that.
            const resetSubscriptions = resetOnLayerShowingChange.map((showingIdSignal) =>
                toObservable(showingIdSignal)
                    .pipe(distinctUntilChanged(), skip(1))
                    .subscribe(() => replayExistingAndEmit())
            );

            subscriber.add(() => {
                signalrSubscription.unsubscribe();
                yjsSubscription?.unsubscribe();
                resetSubscriptions.forEach((s) => s.unsubscribe());
            });
        });
    }

    private static readonly LAYER_SLIDE_KEY: Record<1 | 2 | 3, keyof SyncedLessonState> = {
        1: 'primarySlide',
        2: 'secondarySlide',
        3: 'tertiarySlide',
    };

    private batchToCommand(batchJson: string, layer: 1 | 2 | 3, elementType?: SlideTypeCategory): UpdateSlideContentCommand | null {
        try {
            const mutations: DomMutation[] = JSON.parse(batchJson);
            return {
                mutations,
                commandType: CommandType.UPDATE_SLIDE,
                commandParam: '',
                forSlideId: 0,
                forElementType: elementType ?? this.resolveElementType(layer),
                layer,
                guid: '',
            };
        } catch (err) {
            this.log.error('Failed to parse mutation batch — skipping', err instanceof Error ? err : undefined, { layer });
            return null;
        }
    }

    private resolveElementType(layer: 1 | 2 | 3): SlideTypeCategory {
        const slideKey = SlidePreviewCommandStreamService.LAYER_SLIDE_KEY[layer];
        const slideJson = this.lessonYjs.getSnapshot()?.[slideKey] ?? null;
        if (!slideJson) return 'canvas';
        try {
            return extractSlideTypeCategory(JSON.parse(slideJson as string) as IDisplaySlide);
        } catch (err) {
            this.log.error('Failed to resolve element type — falling back to canvas', err instanceof Error ? err : undefined, { layer });
            return 'canvas';
        }
    }
}
